import { useSyncExternalStore } from "react";
import { TOOL_BELT_SLOTS } from "odyssey-shared";
import { isUnboundCode, type ToolActionId } from "../controls/actions.ts";
import { pageKeys } from "../controls/page.ts";
import type { KeyTable } from "../controls/page.ts";

/**
 * The keys the tool belt draws on its slots (ADR-142 decision 3b): each slot's own key where its Tool 1 to Tool 4 action
 * is bound, which is none by default, and, while none of the four is bound, the key that cycles tools on slot 1, as the
 * mockup draws Q there. A slot with no key to show is null.
 *
 * @param keys The key table.
 * @returns Each slot's label, slot 1 first.
 */
export function toolKeyLabels(keys: Pick<KeyTable, "code" | "label">): readonly (string | null)[] {
  const own: (string | null)[] = Array.from({ length: TOOL_BELT_SLOTS }, (_, slot): string | null => {
    const action = `tool${slot + 1}` as ToolActionId;
    return isUnboundCode(keys.code(action)) ? null : keys.label(action);
  });
  const none = !own.some((label) => label !== null);
  if (none) {
    own[0] = isUnboundCode(keys.code("toolCycle")) ? null : keys.label("toolCycle");
  }
  return own;
}

/**
 * The belt's key labels as the page's keys stand now, drawn again when the table or the layout changes.
 *
 * @param keys The key table; the page's unless given.
 * @returns Each slot's label.
 */
export function useToolKeys(keys: KeyTable = pageKeys): readonly (string | null)[] {
  const joined = useSyncExternalStore(
    keys.subscribe,
    () => toolKeyLabels(keys).join("\u0000"),
    () => toolKeyLabels(keys).join("\u0000"),
  );
  return joined.split("\u0000").map((label) => (label === "" ? null : label));
}
